/**
 * Hook for managing org-level knowledge assets.
 *
 * Provides CRUD operations, file upload, URL creation,
 * and cache management via React Query.
 */

import { useCallback, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import knowledgeAssetService, {
  detectKnowledgeAssetFileType,
  type KnowledgeAssetUrlType,
} from '@/lib/api/knowledgeAssets';

export function useKnowledgeAssets(organizationId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['knowledge-assets', organizationId];

  // Fetch all assets for the org
  const {
    data: listData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await knowledgeAssetService.listAssets(organizationId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch knowledge assets');
      }
      return response.data;
    },
    enabled: !!organizationId,
    refetchInterval: (query) => query.state.data?.assets?.some(a => a.processing_status === 'processing' || a.processing_status === 'pending') ? 5000 : false,
  });

  const assets = listData?.assets ?? [];
  const error = queryError instanceof Error ? queryError.message : null;

  /**
   * Upload a file: request signed URL → upload → create asset record.
   */
  const uploadFileMutation = useMutation({
    mutationFn: async ({ file, title }: { file: File; title?: string }) => {
      const fileType = detectKnowledgeAssetFileType(file);

      if (!fileType) {
        throw new Error('Unsupported file type. Only PDF, Markdown, text, and video files are supported.');
      }

      // 1. Request signed upload URL
      const urlResponse = await knowledgeAssetService.requestUploadUrl(
        organizationId,
        file.name,
        fileType
      );
      if (!urlResponse.success || !urlResponse.data) {
        throw new Error(urlResponse.error || 'Failed to get upload URL');
      }

      const { storage_path, upload_url } = urlResponse.data;

      // 2. Upload file to signed URL
      const uploaded = await knowledgeAssetService.uploadToSignedUrl(upload_url, file);
      if (!uploaded) {
        throw new Error('File upload failed');
      }

      // 3. Create asset record (triggers processing)
      const createResponse = await knowledgeAssetService.createFileAsset(
        organizationId,
        storage_path,
        file.name,
        fileType,
        title,
        file.size
      );
      if (!createResponse.success || !createResponse.data) {
        throw new Error(createResponse.error || 'Failed to create asset');
      }

      return createResponse.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Asset Uploaded', description: 'Processing will begin shortly.' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Upload Failed', description: err.message });
    },
  });

  /**
   * Add a URL as a knowledge asset.
   */
  const addUrlMutation = useMutation({
    mutationFn: async ({ url, title, urlType }: { url: string; title?: string; urlType?: KnowledgeAssetUrlType }) => {
      const response = await knowledgeAssetService.createUrlAsset(organizationId, url, title, urlType);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create URL asset');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'URL Added', description: 'Processing will begin shortly.' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Failed to Add URL', description: err.message });
    },
  });

  /**
   * Update asset title or summary.
   */
  const updateMutation = useMutation({
    mutationFn: async ({ assetId, updates }: { assetId: string; updates: { title?: string; summary?: string } }) => {
      const response = await knowledgeAssetService.updateAsset(assetId, updates);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update asset');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Update Failed', description: err.message });
    },
  });

  /**
   * Delete an asset.
   */
  const deleteMutation = useMutation({
    mutationFn: async (assetId: string) => {
      const response = await knowledgeAssetService.deleteAsset(assetId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete asset');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Asset Deleted' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Delete Failed', description: err.message });
    },
  });

  /**
   * Refresh a URL asset (re-extract content).
   */
  const refreshMutation = useMutation({
    mutationFn: async (assetId: string) => {
      const response = await knowledgeAssetService.refreshAsset(assetId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to refresh asset');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'URL Refreshed', description: 'Content has been re-extracted.' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Refresh Failed', description: err.message });
    },
  });

  // Track parallel uploads with a counter since useMutation.isPending
  // only reflects the latest call when invoked concurrently.
  const uploadCountRef = useRef(0);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  const uploadFile = useCallback(
    async (file: File, title?: string) => {
      uploadCountRef.current++;
      setIsUploadingFiles(true);
      try {
        return await uploadFileMutation.mutateAsync({ file, title });
      } finally {
        uploadCountRef.current--;
        if (uploadCountRef.current === 0) {
          setIsUploadingFiles(false);
        }
      }
    },
    [uploadFileMutation]
  );

  const addUrl = useCallback(
    (url: string, title?: string, urlType?: KnowledgeAssetUrlType) =>
      addUrlMutation.mutateAsync({ url, title, urlType }),
    [addUrlMutation]
  );

  const updateAsset = useCallback(
    (assetId: string, updates: { title?: string; summary?: string }) =>
      updateMutation.mutateAsync({ assetId, updates }),
    [updateMutation]
  );

  const deleteAsset = useCallback(
    (assetId: string) => deleteMutation.mutateAsync(assetId),
    [deleteMutation]
  );

  const refreshAsset = useCallback(
    (assetId: string) => refreshMutation.mutateAsync(assetId),
    [refreshMutation]
  );

  return {
    assets,
    isLoading,
    error,
    uploadFile,
    addUrl,
    updateAsset,
    deleteAsset,
    refreshAsset,
    isUploading: isUploadingFiles,
    isAddingUrl: addUrlMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isRefreshing: refreshMutation.isPending,
  };
}
